import * as React from 'react'
import { graphql, HeadProps, PageProps } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import MapSlicesToComponents from '@walltowall/react-map-slices-to-components'

import { PickPartial } from '../types'
import { MapDataToPropsEnhancerArgs } from '../lib/mapSlicesToComponents'
import { pairsEq } from '../lib/pairsEq'
import { slicesMap } from '../slices/PageBody'

import { Layout } from '../components/Layout'
import { useSiteSettings } from '../hooks/useSiteSettings'

/**
 * `listMiddleware` for `react-map-slices-to-components`. Add or modify slices
 * for the page here.
 *
 * @see https://github.com/WalltoWall/react-map-slices-to-components#change-the-list-of-slices
 */
// prettier-ignore
export const slicesMiddleware = <T,>(list: T[]) => [
  { __typename: 'PageBodyHeader', id: 'header' },
  ...list,
  { __typename: 'PageBodyFooter', id: 'footer' },
]

/**
 * `mapDataToPropsEnhancer` for `react-map-slices-to-components`. Props defined
 * here are added to all slices.
 *
 * @see https://github.com/WalltoWall/react-map-slices-to-components#providing-global-enhancers
 */
export const mapDataToPropsEnhancer = (
	props: Record<string, unknown> | undefined,
	{
		context,
		previousContext = {},
		nextContext = {},
		previousType,
		nextType,
		previousData,
	}: MapDataToPropsEnhancerArgs,
) => ({
	id:
		previousType === 'PageBodyAnchor'
			? (previousData?.primary?.id as string)
			: undefined,
	previousSharesBg: pairsEq(
		previousContext.bgBottom ?? previousContext.bg,
		context.bgTop ?? context.bg,
	) as boolean | boolean[],
	previousOverhangs: previousContext.overhangsBottom as boolean | boolean[],
	previousIsHeader: previousType === 'PageBodyHeader',
	nextSharesBg: pairsEq(
		context.bgBottom ?? context.bg,
		nextContext.bgTop ?? nextContext.bg,
	) as boolean | boolean[],
	nextOverhangs: nextContext.overhangsTop as boolean | boolean[],
	nextIsFooter: nextType === 'PageBodyFooter',
	...props,
})

/**
 * Props added to all slices by `mapDataToPropsEnhancer` for `PageTemplate`.
 * Intersect this type with a slice's known props to get a complete list of
 * available props.
 *
 * @see https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#intersection-types
 */
export type PageTemplateEnhancerProps = PickPartial<
	ReturnType<typeof mapDataToPropsEnhancer>,
	// Props defined in the enhancer function are marked as optional to remove
	// their necessity when using slices as React components.
	| 'id'
	| 'previousOverhangs'
	| 'previousSharesBg'
	| 'nextOverhangs'
	| 'nextSharesBg'
>

export const PageTemplate = ({
	data,
	location,
}: PageProps<Queries.PageTemplateQuery>) => {
	const page = data?.prismicPage

	/**
	 * Metadata made available in a slice's `mapDataToProps` and
	 * `mapDataToContext` functions.
	 *
	 * @see https://github.com/angeloashmore/react-map-to-components#maptocomponents
	 */
	const meta = React.useMemo(
		() => ({
			rootData: data,
			location,
		}),
		[data, location],
	)

	return (
		<Layout>
			{/* HACK: I know this sucks but we just need to be ADA compliant for some */}
			{/* pages that don't have certain blocks that include <h1></h1> elements */}
			{location.pathname === '/menu/' && <h1 className="sr-only">Menu</h1>}

			<MapSlicesToComponents
				//@ts-ignore
				list={page?.data?.body}
				map={slicesMap}
				meta={meta}
				listMiddleware={slicesMiddleware}
				mapDataToPropsEnhancer={mapDataToPropsEnhancer}
			/>
		</Layout>
	)
}

export const Head = ({ data }: HeadProps<Queries.PageTemplateQuery>) => {
	const siteSettings = useSiteSettings()
	const page = data?.prismicPage

	return (
		<>
			<html lang="en" />
			<title>
				{page?.data?.meta_title ?? page?.data?.title?.text ?? ''}
				{page?.uid === 'home' ? '' : ` - ${siteSettings.siteName}`}
			</title>

			{page?.data?.meta_description && (
				<meta
					name="description"
					content={page?.data?.meta_description ?? siteSettings.siteDescription}
				/>
			)}
		</>
	)
}

export default withPrismicPreview(PageTemplate)

export const query = graphql`
	query PageTemplate($uid: String!) {
		prismicPage(uid: { eq: $uid }) {
			_previewable
			...PrismicPageParentRecursive
			data {
				title {
					text
				}
				meta_title
				meta_description
				body {
					__typename
					...SlicesPageBody
				}
			}
		}
	}
`
